import React, { useContext } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { generatePath, useHistory } from 'react-router-dom';

import type { ISelectedFolder } from '@/store/types';

import { classList, compareSelectedFolder, filterTree, isChildrenSelected, rgba } from '@/helpers';
import { useAppSelector, useResourceNavigation } from '@/hooks';
import { FolderAnnouncements, FolderDocuments, FolderLinks, SearchContext, FolderItem, TreeFolderAccordion } from '.';
import { SIDEBAR_DELAY } from '@/config/variables';
import { selectFoldersList } from '@/store/selectors/resourcesSelectors';
import { otherColors } from '@/styles/colors';
import { defaultFolderHome } from '@/constants';
import { FolderDatabases } from './FolderDatabases';
import { selectAuthMenuType, selectCurrentClientId } from '@/store/selectors/authSelectors';
import { UserType } from '@/store/enums';
import { combineCurrentClientFolder } from '@/store/combiners/resourcesCombiners';
import routes from '@/config/routes';

const folderTreeSelector = createSelector(
  selectFoldersList,
  selectCurrentClientId,
  selectAuthMenuType,
  (foldersList, currentClientId, userType) => ({
    foldersList,
    userType,
    currentClientFolder: combineCurrentClientFolder(foldersList, currentClientId, userType),
  }),
);

const FolderTree = (): React.ReactElement => {
  const { t } = useTranslation();
  const { search } = useContext(SearchContext);
  const { selectedFolder } = useResourceNavigation();
  const { foldersList, currentClientFolder, userType } = useAppSelector(folderTreeSelector);
  const history = useHistory();

  const folderSignature = (clientId: number): ISelectedFolder => ({
    clientId,
    folderId: clientId,
    folderType: 'client',
  });
  const isSelected = (clientId: number): boolean =>
    !!selectedFolder && compareSelectedFolder(selectedFolder, folderSignature(clientId));

  const createOnSelectHandler = (clientId: number) => () => {
    if (!isSelected(clientId)) {
      history.push(generatePath(routes.sp.scheduling.resources.client, { client: clientId }));
    }
  };

  const treeToShow = userType === UserType.agent ? foldersList || [] : currentClientFolder ? [currentClientFolder] : [];

  return (
    <Container className="accordion-tree">
      {userType === UserType.agent && (
        <FolderItem
          className="accordion-home"
          data={{ folderId: defaultFolderHome.folderId, name: t`resources.leftPanel.folderHome` }}
          resourceDetails={{
            clientId: defaultFolderHome.clientId,
            folderType: defaultFolderHome.folderType,
            parentFolderId: null,
          }}
        />
      )}
      {filterTree(treeToShow, search).map(client => (
        <TreeFolderAccordion
          key={client.clientId}
          title={client.clientName}
          onSelectCallback={createOnSelectHandler(client.clientId)}
          className={classList(isSelected(client.clientId) && 'accordion-title-selected')}
          isChildrenSelected={
            selectedFolder
              ? isChildrenSelected(folderSignature(client.clientId), selectedFolder, foldersList || [])
              : false
          }
        >
          <FolderAnnouncements data={client} />
          <FolderDocuments data={client} />
          <FolderLinks data={client} />
          <FolderDatabases data={client} />
        </TreeFolderAccordion>
      ))}
    </Container>
  );
};

export { FolderTree };

const Container = styled.div`
  transition: opacity ${SIDEBAR_DELAY}ms ease-in-out !important;

  .collapsed & {
    opacity: 0;
  }

  &.accordion-tree {
    font-size: 14px;
    color: #374151;
    position: relative;

    .accordion-home {
      position: relative;

      &::after {
        content: '';
        position: absolute;
        right: 0;
        bottom: 0;
        width: 100%;
        height: 1px;
        background-color: ${rgba(otherColors.lxc.blue, 0.3)};
      }

      &.accordion-title-selected::after {
        content: none;
      }
    }

    .accordion-title {
      font-weight: 500 !important;
      justify-content: flex-start !important;
      align-items: center;
      flex-direction: row-reverse;
      cursor: pointer;

      span {
        padding-left: 11px;
      }

      .accordion-chevron {
        height: 12px;
        transition: transform ${SIDEBAR_DELAY / 2}ms;
      }

      &.accordion-open > .accordion-chevron,
      &.accordion-title-selected .accordion-chevron {
        transform: rotate3d(0, 0, 1, 90deg);
      }
    }

    > .accordion .accordion,
    > .accordion .accordion-item {
      margin-left: 14px;
      position: relative;
      border-radius: 8px;
    }

    > .accordion .accordion-item {
      &:hover {
        background-color: #e9ecef;
      }
    }
  }
`;
